import * as React            from 'react';
import {cx}                  from 'emotion';
import {cs}                  from '@Common/react/chain-services';

import {FontAwesomeIcon}   from "@fortawesome/react-fontawesome";
import {faCheck,
         faSpinner}        from "@fortawesome/free-solid-svg-icons";

import {values}                       from "@Common/utils/objects";
import {ReactGAGuestEvent}            from '@Common/libs/ga/xd-react-ga';
import {scopedState}                  from '@Common/react/scoped-state';
import {UseState}                     from '@Common/react/use-state';
import {scrollIntoView}               from '@Common/ui-components/light/scroll-into-view';
import {ReCaptcha}                    from '@Common/ui-components/recaptcha/recaptcha';
import {removeURLParameterFromWindow} from '@Common/utils/remove-url-parameter';

import {ConfirmDialog}                from '../../../../../../common/ui-components/heavy/common/design2/dialogs/confirm-dialog/confirm-dialog';
import {PasswordInput}                from '../../../../../../common/ui-components/password-input/password-input';
import {consumeContext}               from '../../../../../../common/react/context';
import {GoogleLoginButton}            from '../google-login-button';
import {FacebookLoginButton}          from '../facebook-login-button';

import "./guest-signin-modal.scss";

declare global {
    interface Window {
        onCaptchaLoad   ?: () => void,
    }
}

export const GuestSigninModal = ({apiError, values, content, domRef, closeModal}) => cs(
    consumeContext("apis"),
    consumeContext("company"),
    consumeContext("session"),
    consumeContext("routerHistory"),
    ["initValues", ({}, next) => UseState({
        initValue: {
            email: '',
            firstName: '',
            lastName: '',
            password: '',
            confirmPassword: '',
        },
        next,
    })],
    ["confirmCloseDialog", ({}, next) => ConfirmDialog({next})],
    ["captchaTokenState", ({}, next) => UseState({initValue: null, next})],
    ["validation", ({}, next) => next({
        length: values.value?.password?.length > 0,
    })],
    ["validationLines", ({validation}, next) => next(getValidationLines({validation}))],
    ["submit", ({}, next) => UseState({initValue: false, next})],
    ["submitting", ({}, next) => UseState({initValue: false, next})],
    ({apis, company, captchaTokenState, validation, submit, submitting, routerHistory}) => (
        <div className="guest-signin-modal-gsm3 guest-signin-modal">
            <div className="content-container">
                <div className="header" ref={domRef.set}>
                    <div className="title" xd-qa-type="label" xd-qa-name="Sign In">
                        Sign In
                    </div>
                    <div className="sub-title">
                        Sign in to your Xpressdocs account
                    </div>
                </div>
                <hr className="divider" />
                {apiError.value?.message && <><div className="error-message" xd-qa-type="text" xd-qa-name="Error Message">
                    {apiError.value.message}
                </div><br /></>}
                {apiError.value?.data?.idUser && <><a
                    className="link"
                    onClick={async () => {
                        submitting.onChange(true);
                        removeURLParameterFromWindow({parameter: "modal"})
                        try {
                            let data = await apis.next.authenticate.login({
                                existingAccount: true,
                            })
                            data.statusCode === "success" ? (
                                ReactGAGuestEvent({
                                    eventName: "login",
                                    method: "username",
                                    reason: data.message,
                                    statusCode: data.statusCode
                                }),
                                window.location.replace("/app")
                            ) : (
                                ReactGAGuestEvent({
                                    eventName: "login_failed",
                                    method: "username",
                                    reason: data.message,
                                    statusCode: data.statusCode
                                }),
                                apiError.onChange(data),
                                scrollIntoView(domRef.get()),
                                grecaptcha.reset()
                            )
                        } catch(e) {
                            console.log(e)
                        }
                        submitting.onChange(false);
                    }}
                    xd-qa-type="link"
                    xd-qa-name="Log in Different Storefront"
                >
                    Log in to my existing account
                </a>
                <div className="note" xd-qa-type="label" xd-qa-name="Note">Note: any customizations you have done so far will be lost.</div>
                <br />
                </>}

                {apiError.value?.data?.includeLink && <a
                    className="link"
                    onClick={() => {
                        scopedState(values, ["password"]).onChange("");
                        scopedState(values, ["confirmPassword"]).onChange("");
                        apiError.onChange(null)
                        scopedState(content, ["content"]).onChange("newAccount");
                    }}
                    xd-qa-type="link"
                    xd-qa-name="Create Account"
                >
                    Create an account in {company.companyName} with a new username
                </a>}
                <div className="info-section">
                    <div className={cx("user-email section", {error: fields.username.showError})}>
                        <div className="email-label label">
                            {fields.username.label}
                        </div>
                        <div className="email-input input">
                            <input {...{
                                value: scopedState(values, ["username"]).value,
                                onChange: (e) => {
                                    fields.username.showError = false;
                                    let value = e.target.value;
                                    scopedState(values, ["username"]).onChange(value);
                                },
                                autoComplete: "username",
                                className: "value",
                                maxLength: 64,
                                "xd-qa-type": "textbox",
                                "xd-qa-name": "Username",
                            }}/>
                        </div>
                        {submit.value && fields.username.showError && fields.username.errorMessage()}
                    </div>
                    <div className={cx("user-password section", {error: fields.password.showError})}>
                        <div className="password-label label">
                            {fields.password.label}
                            {company?.landingPwRetrieve === 1 && <span className="forgot-password"
                                onClick={() => {
                                    routerHistory.push('/password/')
                                    closeModal()
                                }}
                            >Forgot Username/Password?</span>}
                        </div>
                        <div className="password-input input">
                            {PasswordInput({inputProps:{
                                value: scopedState(values, ["password"]).value,
                                onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                                    fields.password.showError = false;
                                    let value = e.target.value;
                                    scopedState(values, ["password"]).onChange(value);
                                },
                                autoComplete: "current-password",
                                className: "value",
                                maxLength: 64,
                                "xd-qa-name": "Password",
                            }})}
                        </div>
                        {submit.value && fields.password.showError && fields.password.errorMessage(validation)}
                    </div>
                </div>
                <div className="footer">
                    <div className="recaptcha" xd-qa-type="card" xd-qa-name="Recaptcha">
                        <ReCaptcha onExpiration={() => captchaTokenState.onChange(null)} onVerification={(value) => {
                            fields.captcha.showError = false;
                            captchaTokenState.onChange(value)
                        }}/>
                        {submit.value && fields.captcha.showError && fields.captcha.errorMessage()}
                    </div>
                    <button
                        xd-qa-type="button"
                        xd-qa-name="Sign In"
                        className="sign-in"
                        disabled={submitting.value}
                        onClick={async () => {
                            submitting.onChange(true);
                            removeURLParameterFromWindow({parameter: "modal"})
                            if(isValid({fields, values, validation, captchaTokenState})) {
                                try {
                                    let data = await apis.next.authenticate.login({
                                        username: values.value.username,
                                        password: values.value.password,
                                        captcha: captchaTokenState.value,
                                    })
                                    data.statusCode === "success" ? (
                                        ReactGAGuestEvent({
                                            eventName: "login",
                                            method: "username",
                                            reason: data.message,
                                            statusCode: data.statusCode
                                        }),
                                        location.reload()
                                    ) : (
                                        ReactGAGuestEvent({
                                            eventName: "login_failed",
                                            method: "username",
                                            reason: data.message,
                                            statusCode: data.statusCode
                                        }),
                                        apiError.onChange(data),
                                        scrollIntoView(domRef.get()),
                                        grecaptcha.reset()
                                    )
                                } catch(e) {
                                    console.log(e)
                                }
                            } else {
                                submit.onChange(true)
                                grecaptcha.reset()
                            }
                            submitting.onChange(false);
                        }}
                    >
                        {submitting.value ? <FontAwesomeIcon icon={faSpinner} className="fa-pulse"/> : "Sign In"}
                    </button>
                    {(company?.prefs.enableGoogleIdLogin || company?.prefs.enableMetaIdLogin) && <div className="separator">
                        <hr /> <span className="login-text">Or login using</span><hr />
                    </div>}
                    {(company?.prefs.enableGoogleIdLogin) && <div className="social-buttons">
                        {GoogleLoginButton({content, apiError, domRef})}
                    </div>}
                    {(company?.prefs.enableMetaIdLogin) && <div className="social-buttons">
                        {FacebookLoginButton({content, apiError, domRef})}
                    </div>}
                    <div className="label">
                        Don't have an account?<span className="sign-up"><a
                        onClick={() => {
                            scopedState(values, ["password"]).onChange("");
                            scopedState(values, ["confirmPassword"]).onChange("");
                            apiError.onChange(null)
                            scopedState(content, ["content"]).onChange("newAccount");
                        }}
                        xd-qa-type="link"
                        xd-qa-name="Sign Up"
                    > Sign Up</a></span>
                    </div>
                </div>
            </div>
        </div>
    )
)

const getValidationLines = ({validation}) => ([
    {
        "message": "At least 6 characters",
        "validated": validation.length,
    },
    {
        "message": "At least 1 number",
        "validated": validation.number,
    },
    {
        "message": "At least 1 lowercase letter",
        "validated": validation.lowercase,
    },
    {
        "message": "At least 1 uppercase letter",
        "validated": validation.uppercase,
    },
    {
        "message": "At least 1 special character @$!%*#?&",
        "validated": validation.special,
    },
]);

const fields = {
    username: {
        label: "Username",
        valid: (username) => {return username && username.trim() !== ""},
        errorMessage: () => (
            <div className="error-message" xd-qa-type="text" xd-qa-name="Username Error Message">
                Username Required
            </div>
        ),
        showError: false,
    },
    password: {
        label: "Password",
        valid: (validation) => { return values(validation).every(Boolean) },
        errorMessage: (validation) => (
            <div className="error-message" xd-qa-type="text" xd-qa-name="Password Error Message">
                {values(validation).every(Boolean) ? (
                    ""
                ) : "Invalid password"}
            </div>
        ),
        showError: false,
    },
    captcha: {
        valid: (captchaTokenState) => {return captchaTokenState.value},
        errorMessage: () => (
            <div className="error-message" xd-qa-type="text" xd-qa-name="Recaptcha Error Message">
                Captcha Required
            </div>
        ),
        showError: false,
    }
}

function isValid({fields, values, validation, captchaTokenState}) {
    fields.username.showError = !fields.username.valid(values.value.username)
    fields.password.showError = !fields.password.valid(validation)
    fields.captcha.showError  = !fields.captcha.valid(captchaTokenState)

    return fields.username.valid(values.value.username) && fields.password.valid(validation) && captchaTokenState.value
}
